import { useEffect, useState } from "react";

export const DecisionProcessMobile = () => {
  const [nodesVisible, setNodesVisible] = useState<boolean[]>([false, false, false, false]);
  const [lineVisible, setLineVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    // Staggered node appearance
    const timers = [
      setTimeout(() => setNodesVisible([true, false, false, false]), 100),
      setTimeout(() => setNodesVisible([true, true, false, false]), 250),
      setTimeout(() => setNodesVisible([true, true, true, false]), 400),
      setTimeout(() => setNodesVisible([true, true, true, true]), 550),
      setTimeout(() => setLineVisible(true), 650),
    ];
    
    // Cycle through steps
    const stepInterval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 2500);

    return () => {
      timers.forEach(clearTimeout);
      clearInterval(stepInterval);
    };
  }, []);

  const steps = [
    { label: "Kontext" },
    { label: "Optionen" },
    { label: "Trade-offs" },
    { label: "Entscheidung" },
  ];

  // Calculate progress percentage based on active step (0-100)
  const progressPercent = (activeStep / (steps.length - 1)) * 100;

  return (
    <div className="w-full">
      {/* Stepper container */}
      <div className="relative flex items-center justify-between px-4">
        {/* Connection line container */}
        <div className="absolute left-4 right-4 top-4 flex items-center">
          <div className="relative w-full mx-4">
            {/* Background line with draw animation */}
            <div 
              className="absolute inset-0 h-[2px] bg-border/40 rounded-full transition-all duration-700"
              style={{
                transform: lineVisible ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
              }}
            />
            
            {/* Animated progress line */}
            <div 
              className="absolute left-0 top-0 h-[2px] bg-primary rounded-full transition-all duration-700 ease-out"
              style={{ 
                width: lineVisible ? `${progressPercent}%` : '0%',
              }}
            />
          </div>
        </div>

        {/* Step nodes with staggered scale-in */}
        {steps.map((step, index) => {
          const isActive = activeStep === index;
          const isPast = index < activeStep;
          
          return (
            <div
              key={index}
              className={`relative z-10 flex flex-col items-center transition-all duration-500 ${
                nodesVisible[index] ? 'opacity-100 scale-100' : 'opacity-0 scale-0'
              }`}
              style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              {/* Node */}
              <div 
                className={`relative w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${
                  isActive 
                    ? 'bg-primary text-primary-foreground scale-110 shadow-lg shadow-primary/30' 
                    : isPast
                    ? 'bg-primary/80 text-primary-foreground'
                    : 'bg-card border-2 border-border/60 text-muted-foreground'
                }`}
              >
                <span className="text-xs font-semibold">
                  {index + 1}
                </span>
                
                {/* Active pulse ring */}
                {isActive && (
                  <div className="absolute -inset-1.5 rounded-full border-2 border-primary/30 animate-[pulse_2s_ease-in-out_infinite]" />
                )}
              </div>
              
              {/* Label */}
              <span 
                className={`mt-2 text-[10px] font-medium transition-all duration-300 whitespace-nowrap ${
                  isActive 
                    ? 'text-foreground' 
                    : isPast
                    ? 'text-primary'
                    : 'text-muted-foreground/60'
                }`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Subtle hint text */}
      <p 
        className={`text-center text-[10px] text-muted-foreground/50 mt-4 transition-opacity duration-500 ${
          nodesVisible[3] ? 'opacity-100' : 'opacity-0'
        }`}
      >
        4 Phasen zur verbindlichen Entscheidung
      </p>
    </div>
  );
};
